'use client'

import React from 'react'

import { ImageWrapper } from '@/components/image'
import { ImageSizingProps, ImageSource, ImgProps } from '@/components/image/types'
import { Skeleton } from '@/components/server/shared/skeleton'

// Defers the markdown image until it scrolls near the viewport (200px margin).
// MUST be a client component — it uses useState/useEffect/IntersectionObserver.
// It previously lived inline in the server-rendered markdown.tsx, where calling
// React.useState on the server threw "useState is not a function" on any story
// whose markdown contained an image. Kept as its own 'use client' module so the
// hook usage has a real client boundary.
export const LazyMarkdownImage: React.FC<{
  image: ImageSource
  imgProps?: ImgProps
  sizingProps?: ImageSizingProps
  origin?: string
  className?: string
  blockRobots?: boolean
}> = ({ image, imgProps, sizingProps, origin = 'n4-markdown', className, blockRobots }) => {
  const [isVisible, setIsVisible] = React.useState(false)
  const ref = React.useRef<HTMLSpanElement | null>(null)

  React.useEffect(() => {
    if (isVisible) return
    if (typeof window === 'undefined') {
      setIsVisible(true)
      return
    }
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px 0px' }
    )
    if (ref.current) {
      observer.observe(ref.current)
    }
    return () => observer.disconnect()
  }, [isVisible])

  const aspectRatio =
    sizingProps?.aspectRatio ||
    (sizingProps?.nativeWidth && sizingProps?.nativeHeight && sizingProps?.nativeHeight !== 'auto'
      ? sizingProps.nativeWidth / sizingProps.nativeHeight
      : undefined)

  const wrapperStyle: React.CSSProperties = { position: 'relative', width: '100%', display: 'block' }
  if (aspectRatio) {
    wrapperStyle.aspectRatio = aspectRatio
  } else if (sizingProps?.nativeHeight) {
    wrapperStyle.minHeight = Number(sizingProps.nativeHeight) || sizingProps.nativeHeight
  }

  // Use span instead of div to allow nesting inside <p> tags in markdown
  return (
    <span ref={ref} className={className} style={wrapperStyle}>
      {isVisible ? (
        <ImageWrapper
          image={image}
          imgProps={imgProps}
          sizingProps={sizingProps}
          origin={origin}
          blockRobots={blockRobots}
          loadTime="lazy"
        />
      ) : (
        <span className="skeleton absolute inset-0 flex" style={{ display: 'flex' }}>
          <Skeleton as="span" />
        </span>
      )}
    </span>
  )
}
